import { Timestamp } from 'firebase/firestore';

/**
 * Staff Module Types
 * Comprehensive type definitions for staff/employee management
 */

// Employment types
export type EmploymentType = 'full-time' | 'part-time' | 'contract' | 'freelance';
export type StaffStatus = 'active' | 'inactive' | 'on-leave' | 'terminated';
// Canonical shop roles (2026-08-01). "shop_attendant" (Shop Attendant/Facilitator) —
// NEVER "receptionist". Multi-owner supported; permissions are per-member toggles.
export type StaffRole = 'owner' | 'manager' | 'barber' | 'trainee' | 'shop_attendant' | 'accountant';

// Career progression stages
export type CareerStage =
  | 'Apprentice'
  | 'Junior'
  | 'Barber'
  | 'Senior'
  | 'Master'
  | 'Elite'
  | 'Partner';

/**
 * Staff Member Interface
 * Core staff data structure
 */
export interface StaffMember {
  id: string;

  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  photo?: string;
  dateOfBirth?: string;
  address?: StaffAddress;

  // Employment Details
  role: StaffRole;
  employmentType: EmploymentType;
  status: StaffStatus;
  hireDate: Date | Timestamp;
  terminationDate?: Date | Timestamp;
  department?: string;

  // Professional Details
  skills: string[];
  certifications: Certification[];
  specialties: string[];
  experience: number; // years
  languages: string[];
  bio?: string;

  // Schedule & Availability
  availability: WeeklyAvailability;
  vacationDays: number;
  sickDays: number;

  // Compensation
  compensation: CompensationDetails;

  // Career Progression
  careerStage: CareerStage;
  equity?: number;

  // Performance Metrics
  metrics: StaffMetrics;

  // System Fields
  colorCode?: string;
  notes?: string;
  emergencyContact?: EmergencyContact;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;

  // Emergency Booking Settings (Admin-controlled)
  emergencyBooking?: {
    enabled: boolean;           // Whether staff can take emergency bookings
    enabledAt?: Date | Timestamp;
    enabledBy?: string;         // Admin who enabled
    unlockReason?: 'bpi_threshold' | 'manual_grant';
  };
}

/**
 * Staff Address
 */
export interface StaffAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

/**
 * Certification
 */
export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: Date | Timestamp;
  expiryDate?: Date | Timestamp;
  verified: boolean;
}

/**
 * Weekly Availability
 */
export interface WeeklyAvailability {
  monday: DayAvailability;
  tuesday: DayAvailability;
  wednesday: DayAvailability;
  thursday: DayAvailability;
  friday: DayAvailability;
  saturday: DayAvailability;
  sunday: DayAvailability;
}

export interface DayAvailability {
  available: boolean;
  start: string; // HH:MM format
  end: string;   // HH:MM format
  breaks?: BreakTime[];
}

export interface BreakTime {
  start: string;
  end: string;
  type: 'lunch' | 'short' | 'other';
}

/**
 * Compensation Details
 */
export interface CompensationDetails {
  type: 'hourly' | 'salary' | 'commission' | 'hybrid';
  baseRate?: number;        // Hourly rate or annual salary
  commissionRate: number;   // Percentage (0-100)

  // Sliding scale commission tiers
  commissionTiers?: CommissionTier[];
  currentTier?: CommissionTier;

  // Bonuses
  bonuses: Bonus[];

  // Payment details
  paymentMethod: 'direct-deposit' | 'check' | 'cash';
  payFrequency: 'weekly' | 'bi-weekly' | 'monthly';
}

export interface CommissionTier {
  minRevenue: number;
  maxRevenue: number;
  rate: number;
}

export interface Bonus {
  id: string;
  type: 'performance' | 'holiday' | 'referral' | 'retention' | 'other';
  amount: number;
  date: Date | Timestamp;
  description: string;
  paid: boolean;
}

/**
 * Staff Metrics
 */
export interface StaffMetrics {
  // Performance
  avgRating: number;
  totalReviews: number;
  avgServiceTime: number;  // minutes

  // Revenue
  monthlyRevenue: number;
  ytdRevenue: number;
  avgTicket: number;

  // Clients
  totalClients: number;
  returningClients: number;
  clientRetentionRate: number;
  newClientsThisMonth: number;

  // Productivity
  appointmentsCompleted: number;
  appointmentsCancelled: number;
  noShows: number;

  // BPI (Barber Performance Index)
  bpiScore: number;
  bpiFactors?: BPIFactors;
}

export interface BPIFactors {
  punctuality: number;
  clientSatisfaction: number;
  productivity: number;
  revenue: number;
  retention: number;
  teamwork: number;
}

/**
 * Emergency Contact
 */
export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

/**
 * Staff Filter Options
 */
export interface StaffFilters {
  status?: StaffStatus[];
  role?: StaffRole[];
  careerStage?: CareerStage[];
  department?: string;
  search?: string;
}

/**
 * Staff Statistics Summary
 */
export interface StaffStatistics {
  totalStaff: number;
  activeStaff: number;
  onLeave: number;
  avgRating: number;
  avgCommission: number;
  totalMonthlyRevenue: number;
  avgBpiScore: number;
  topPerformers: string[];
}

/**
 * Staff Schedule Event
 */
export interface StaffScheduleEvent {
  id: string;
  staffId: string;
  date: string;
  type: 'shift' | 'time-off' | 'training' | 'meeting';
  startTime: string;
  endTime: string;
  notes?: string;
  approved: boolean;
  approvedBy?: string;
}

/**
 * Time Off Request
 */
export interface TimeOffRequest {
  id: string;
  staffId: string;
  staffName: string;
  type: 'vacation' | 'sick' | 'personal' | 'unpaid';
  startDate: string;
  endDate: string;
  totalDays: number;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  requestedAt: Date | Timestamp;
  reviewedAt?: Date | Timestamp;
  reviewedBy?: string;
  reviewNotes?: string;
}

/**
 * Staff Document
 */
export interface StaffDocument {
  id: string;
  staffId: string;
  type: 'contract' | 'id' | 'certification' | 'tax-form' | 'other';
  name: string;
  url: string;
  uploadedAt: Date | Timestamp;
  expiresAt?: Date | Timestamp;
}

/**
 * Default availability template
 */
export const DEFAULT_AVAILABILITY: WeeklyAvailability = {
  monday: { available: true, start: '09:00', end: '18:00' },
  tuesday: { available: true, start: '09:00', end: '18:00' },
  wednesday: { available: true, start: '09:00', end: '18:00' },
  thursday: { available: true, start: '09:00', end: '18:00' },
  friday: { available: true, start: '09:00', end: '18:00' },
  saturday: { available: true, start: '09:00', end: '17:00' },
  sunday: { available: false, start: '', end: '' }
};

/**
 * Career stage progression order
 */
export const CAREER_STAGES: CareerStage[] = [
  'Apprentice',
  'Junior',
  'Barber',
  'Senior',
  'Master',
  'Elite',
  'Partner'
];

/**
 * Role display names
 */
export const ROLE_LABELS: Record<StaffRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  barber: 'Barber',
  trainee: 'Trainee',
  shop_attendant: 'Shop Attendant',
  accountant: 'Accountant'
};

/**
 * Status display configuration
 */
export const STATUS_CONFIG: Record<StaffStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'green' },
  inactive: { label: 'Inactive', color: 'gray' },
  'on-leave': { label: 'On Leave', color: 'yellow' },
  terminated: { label: 'Terminated', color: 'red' }
};
